'use client';

import TextareaWithButton from '@/components/features/questionnaire/components/QuestionTextArea';
import { ConversationResource } from '@/types';
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { messageVariants } from '@/lib/helpers/animations/variants';
import { XIcon, ClipboardListIcon } from 'lucide-react';
import clsx from 'clsx';

// Local storage key for storing the dismissed state
const SUBMISSION_DISMISSED_KEY = 'jaqln_submission_confirmation_dismissed';

// Helper to check if we're in development mode
const isDevelopment = process.env.NODE_ENV === 'development';

export interface InputAreaProps {
  isQuestionnaireSubmitted: boolean;
  conversationData: ConversationResource | null | undefined;
  handleFormGoBack: () => void;
  inputValue: string;
  setInputValue: (value: string) => void;
  handlePromptSubmission: (text: string, formAnswerId?: string) => void;
  formAnswerId: string;
  t: any;
  isChatLoading: boolean;
  isStreaming: boolean;
  promptInputRef: React.RefObject<HTMLTextAreaElement>;
}

export const InputArea = React.memo((props: InputAreaProps) => {
  const {
    isQuestionnaireSubmitted,
    conversationData,
    handleFormGoBack,
    inputValue,
    setInputValue,
    handlePromptSubmission,
    formAnswerId,
    t,
    isChatLoading,
    isStreaming,
    promptInputRef,
  } = props;

  const [showSubmissionConfirmation, setShowSubmissionConfirmation] =
    useState(true);

  // Check localStorage on mount to see if user previously dismissed the message
  useEffect(() => {
    // Only check localStorage in development mode and in client-side environment
    if (isDevelopment && typeof window !== 'undefined') {
      const isDismissed =
        localStorage.getItem(SUBMISSION_DISMISSED_KEY) === 'true';
      if (isDismissed) {
        setShowSubmissionConfirmation(false);
      }
    }
  }, []);

  useEffect(() => {
    if (isChatLoading) {
      // In development mode, respect the user's preference
      if (isDevelopment && typeof window !== 'undefined') {
        const isDismissed =
          localStorage.getItem(SUBMISSION_DISMISSED_KEY) === 'true';
        if (!isDismissed) {
          setTimeout(() => {
            setShowSubmissionConfirmation(true);
          }, 1000);
        }
      } else {
        // In production, always show the confirmation
        setTimeout(() => {
          setShowSubmissionConfirmation(true);
        }, 1000);
      }
    }
  }, [isChatLoading]);

  // Handle dismissing the confirmation message
  const handleDismissConfirmation = () => {
    setShowSubmissionConfirmation(false);

    // In development mode, save the dismissed state to localStorage
    if (isDevelopment && typeof window !== 'undefined') {
      localStorage.setItem(SUBMISSION_DISMISSED_KEY, 'true');
    }
  };
  const MAX_LENGTH = 1000;

  return (
    <>
      {isQuestionnaireSubmitted &&
        showSubmissionConfirmation &&
        (!conversationData ||
          (conversationData?.advices?.length ?? 0) === 0) && (
          <motion.div
            variants={messageVariants}
            className='mx-auto mb-2 w-full px-2 sm:px-4'
          >
            <div className='flex items-center justify-between rounded-lg border border-gray-300 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-900 sm:p-4'>
              <div className='flex-1 overflow-hidden'>
                <p className='text-sm text-gray-600 dark:text-gray-300'>
                  {t(
                    'formQuestionnaire.messages.submissionConfirmation.confirmation'
                  )}
                </p>
                <div
                  className='mt-1 flex cursor-pointer flex-wrap items-center gap-1 text-sm text-purple-600 hover:underline dark:text-purple-400'
                  onClick={handleFormGoBack}
                >
                  <span>
                    {t(
                      'formQuestionnaire.messages.submissionConfirmation.instructions1'
                    )}
                  </span>
                  <ClipboardListIcon className='h-4 w-4' />
                </div>
              </div>
              <button
                className='ml-3 flex h-8 w-8 items-center justify-center rounded-full text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20'
                onClick={handleDismissConfirmation}
                aria-label='Dismiss confirmation'
              >
                <XIcon className='h-5 w-5' />
              </button>
            </div>
          </motion.div>
        )}

      <div className='mx-auto w-full px-2 sm:px-4'>
        <motion.div
          variants={messageVariants}
          className='overflow-hidden rounded-xl border border-purple-100 bg-white shadow-lg dark:border-purple-900/50 dark:bg-gray-900/50'
        >
          <TextareaWithButton
            shouldTextAreaBeAvailable={
              isQuestionnaireSubmitted ||
              (conversationData?.advices?.length ?? 0) > 0
            }
            inputValue={inputValue}
            setInputValue={setInputValue}
            handlePromptSubmission={handlePromptSubmission}
            handleFormGoBack={handleFormGoBack}
            formAnswerId={formAnswerId}
            t={t}
            isLoading={isChatLoading || isStreaming}
            isDisabled={isStreaming}
            promptInputRef={promptInputRef}
          />
        </motion.div>
      </div>

      <div
        className={clsx(
          'mx-auto mb-2 mt-1 w-full px-4 text-[10px] font-medium uppercase tracking-wider sm:px-6',
          inputValue.length === MAX_LENGTH
            ? 'text-red-600 animate-pulse'
            : 'text-gray-400 dark:text-gray-500'
        )}
      >
        <span>
          {t('chat.input.chatCharacterLimitNotice')} {inputValue.length}/
          {MAX_LENGTH}
        </span>
      </div>
    </>
  );
});

InputArea.displayName = 'InputArea';
